// Public landing page shown at "/" to logged-out web visitors (docs/FIX-WEBSITE.md).
// Explains what the platform does, lets members jump to the app stores, and
// lets church leaders leave their details via the "Show me around" form at
// the bottom (#start), which posts to the same submit-church-enquiry edge
// function the rest of the app's enquiry flows already use.
//
// Logged-in users never see this page (App.js redirects them to /app).
// Median users skip it too (App.js sends them straight to /login).
//
// Styles live in LandingPage.css, scoped under .landing-page — this page
// renders standalone, before BibleApp.jsx's own global CSS vars would ever
// be mounted, so it carries its own copy of the brand tokens.
import { useEffect, useMemo, useRef, useState } from 'react'
import './LandingPage.css'

const APP_STORE_URL  = 'https://apps.apple.com/gb/app/id6776408991'
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=uk.co.thbiblecommunity.app'

const SUPABASE_URL      = process.env.REACT_APP_SUPABASE_URL || ''
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || ''

// Real YouTube thumbnail for the New Life Church sermon referenced in the
// demo screens below ("Disciple as a discipler", Sunday 25 Oct) — per
// docs/FIX-WEBSITE.md item 5, not a cropped placeholder image.
const NL_THUMB_URL = 'https://i.ytimg.com/vi/PjDwyWdqDw0/hqdefault.jpg'

export default function LandingPage({ onNavigate }) {
  function go(path) {
    return (e) => { e?.preventDefault?.(); onNavigate?.(path) }
  }
  function scrollToId(id) {
    return (e) => {
      e?.preventDefault?.()
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    function onScroll() { setScrolled(window.scrollY > 8) }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div className="landing-page">
      <div className="hero-glow" aria-hidden="true"/>

      <nav className={scrolled ? 'scrolled' : ''}>
        <div className="wrap nav-in">
          <a className="brand" href="#top" onClick={scrollToId('top')} aria-label="The Bible Community home">
            <BrandMark/>
            The Bible Community
          </a>
          <div className="nav-links">
            <a href="#how" onClick={scrollToId('how')}>How it works</a>
            <a href="#try" onClick={scrollToId('try')}>Sermon clips</a>
            <a href="#community" onClick={scrollToId('community')}>Community</a>
            <a href="#serving" onClick={scrollToId('serving')}>Serving</a>
            <a href="#map" onClick={scrollToId('map')}>For churches</a>
            <a href="#faq" onClick={scrollToId('faq')}>FAQ</a>
            <a className="nav-login" href="/login" onClick={go('/login')}>Log in</a>
          </div>
          <a className="btn btn-ink nav-cta" href="#start" onClick={scrollToId('start')}>Get set up</a>
        </div>
      </nav>

      <Hero id="top"/>
      <HowItWorks/>
      <TryIt/>
      <Community/>
      <Serving/>
      <ForChurches/>
      <ChurchesSetup/>
      <Faq/>
      <LeadForm/>
      <Footer onNavigate={onNavigate} scrollToId={scrollToId}/>
    </div>
  )
}

function BrandMark() {
  return (
    <svg viewBox="0 0 100 100" aria-hidden="true">
      <circle cx="50" cy="62" r="19" fill="#E8C87A"/>
      <g stroke="#E8C87A" strokeWidth="5" strokeLinecap="round">
        <line x1="50" y1="24" x2="50" y2="14"/><line x1="72" y1="34" x2="80" y2="26"/><line x1="28" y1="34" x2="20" y2="26"/>
        <line x1="78" y1="52" x2="88" y2="52"/><line x1="22" y1="52" x2="12" y2="52"/>
      </g>
      <path d="M50 58 L50 92 L18 92 Q14 92 14 86 L14 64 Q14 58 20 58 Z" fill="#1F1B16"/>
      <path d="M50 58 L50 92 L82 92 Q86 92 86 86 L86 64 Q86 58 80 58 Z" fill="#3A332C"/>
    </svg>
  )
}

function AppleIcon() {
  return <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" aria-hidden="true"><path d="M16.4 12.9c0-2.2 1.8-3.3 1.9-3.3-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.6.8-3.3.8-.7 0-1.7-.8-2.8-.8-1.4 0-2.8.8-3.5 2.1-1.5 2.6-.4 6.5 1.1 8.6.7 1 1.5 2.2 2.6 2.2 1 0 1.4-.7 2.7-.7 1.3 0 1.6.7 2.7.6 1.1 0 1.8-1 2.5-2 .8-1.2 1.1-2.3 1.1-2.4-.1 0-2.1-.8-2.1-3.2zM14.3 6.3c.6-.7 1-1.7.9-2.7-.8 0-1.9.6-2.5 1.3-.5.6-1 1.6-.9 2.6.9.1 1.8-.5 2.5-1.2z"/></svg>
}
function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path fill="#E8C87A" d="M3.6 2.3c-.2.2-.3.5-.3.9v17.6c0 .4.1.7.3.9l9.3-9.7L3.6 2.3z"/>
      <path fill="#F6F2EC" d="M16.5 8.4 13 11.9l3.5 3.6 4-2.3c.6-.4.6-1.3 0-1.6l-4-2.3z"/>
      <path fill="#B9B4AB" d="M3.6 2.3 12.9 12l3.6-3.6L6.5 2.7c-1-.6-2.2-.7-2.9-.4z"/>
      <path fill="#8A857D" d="M3.6 21.7c.5.2 1.3.1 2.9-.8l10-5.5L13 11.9l-9.4 9.8z"/>
    </svg>
  )
}

function StoreBadges() {
  return (
    <div className="stores">
      <a className="store" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
        <AppleIcon/><span><small>Download on the</small>App Store</span>
      </a>
      <a className="store" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
        <PlayIcon/><span><small>Get it on</small>Google Play</span>
      </a>
    </div>
  )
}

function Hero({ id }) {
  return (
    <header className="hero" id={id}>
      <div className="wrap hero-grid">
        <div>
          <div className="eyebrow">The Bible app connected to your church</div>
          <h1 style={{ marginTop: 18 }}>Your church's teaching, <em>in your Bible.</em></h1>
          <p className="lede">See what your church taught on any verse. Read together through the week. Stay connected between Sundays.</p>
          <div className="hero-ctas"><StoreBadges/></div>
          <div className="hero-note"><span>Free for church members</span><span>Also works on the web</span></div>
        </div>

        <div className="hero-stage" aria-label="The app showing Romans 8 with sermon clips">
          <div className="phone">
            <div className="scr">
              <div className="notch"/>
              <div className="sb"><span>9:41</span><span>●●● ▮</span></div>
              <div className="reader-top"><span className="pill">Romans 8 ▾</span><span className="row"><span className="rbtn">Aa</span><span className="rbtn">⌕</span></span></div>
              <div className="app-pad verse">
                <sup>26</sup>In the same way, the Spirit also helps our weaknesses, for we don't know how to pray as we ought. <sup>27</sup>He who searches the hearts knows what is on the Spirit's mind. <sup>28</sup><span className="hl">We know that all things work together for good for those who love God</span><span className="dot"/> <sup>29</sup>For whom he foreknew, he also predestined…
              </div>
              <div className="sheet">
                <div className="grab"/>
                <div style={{ fontWeight: 800, fontSize: 14 }}>Romans 8:28</div>
                <div className="row" style={{ marginTop: 10, background: 'var(--clip)', borderRadius: 14, padding: '9px 10px' }}>
                  <div className="faces"><span style={{ background: '#E8C87A' }}>NL</span><span style={{ background: '#C9DDF3' }}>GC</span><span style={{ background: '#BFE0C2' }}>HC</span></div>
                  <div style={{ fontWeight: 800, color: 'var(--clip-ink)', fontSize: 13 }}>▶ 43 sermon clips</div>
                  <div style={{ marginLeft: 'auto', color: 'var(--clip-ink)' }}>›</div>
                </div>
                <div className="row" style={{ marginTop: 12, justifyContent: 'space-between' }}>
                  <div className="swatches"><i style={{ background: '#F2D98A', boxShadow: '0 0 0 2px var(--ink)' }}/><i style={{ background: '#BFE0C2' }}/><i style={{ background: '#C9DDF3' }}/><i style={{ background: '#F3C6C6' }}/></div>
                  <span style={{ fontWeight: 800, fontSize: 12.5 }}>💬 4 · Share</span>
                </div>
              </div>
            </div>
          </div>
          <div className="float f1" style={{ padding: 10, width: 260 }}>
            <div className="thumb img" style={{ height: 136, borderRadius: 14, backgroundImage: `url(${NL_THUMB_URL})` }}><span className="dur">1:12</span></div>
            <div style={{ padding: '10px 4px 2px' }}><span className="badge-clip">▶ Sermon clip</span><b style={{ marginTop: 6, display: 'block' }}>Disciple as a discipler</b><small>New Life Church · Sunday 25 Oct</small></div>
          </div>
          <div className="float f2">
            <div className="eyebrow" style={{ fontSize: 11 }}>This week's plan</div>
            <b style={{ marginTop: 4, display: 'block' }}>Day 3 of 5 · Romans 8</b>
            <div className="row" style={{ marginTop: 8 }}><div className="faces"><span style={{ background: '#F3C6C6' }}>S</span><span style={{ background: '#C9DDF3' }}>T</span><span style={{ background: '#BFE0C2' }}>M</span></div><small>You + 23 others</small></div>
          </div>
          <div className="float f3">
            <div className="row"><span className="clip-ic" style={{ background: '#F3C6C6', color: '#8a3b3b' }}>🙏</span><div><b>Sarah prayed for you</b><small>Pause &amp; pray · 2 min ago</small></div></div>
          </div>
        </div>
      </div>
    </header>
  )
}

const STEPS = [
  { day: 'Sunday', title: "Your sermon goes up on YouTube. We take it from there.", body: "We find the sermon in the livestream, note every Bible passage it touches with the exact minute, and draft a five-day plan. Your leaders approve it with one tap." },
  { day: 'Monday, 7:30', title: "The week's plan arrives, from the sermon they just heard.", body: 'Members choose to join. Five short readings, at their own pace. Nobody is ever "behind".' },
  { day: 'Wednesday', title: 'They read it together, even on different days.', body: "Finish a day and you'll see what your church family took from it. Thoughts, questions and prayers stay inside your church." },
  { day: 'Any day', title: 'Every verse remembers what was preached on it.', body: "Reading Romans in March? Tap a verse and play the moment your pastor preached on it, or hear how other churches taught it." },
]

function HowItWorks() {
  const [active, setActive] = useState(0)
  const stepRefs = useRef([])
  const stepsEl = useRef(null)

  // Scroll-driven on desktop: whichever step crosses the middle of the
  // viewport becomes active, swapping the sticky phone's screen.
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setActive(Number(e.target.dataset.s)) })
    }, { rootMargin: '-45% 0px -45% 0px' })
    stepRefs.current.forEach((el) => el && io.observe(el))
    return () => io.disconnect()
  }, [])

  // Horizontal-scroll-snap carousel on mobile (CSS switches .steps to a
  // row at that breakpoint) — scroll position drives the active dot.
  useEffect(() => {
    const el = stepsEl.current
    if (!el) return
    function onScroll() {
      const i = Math.round(el.scrollLeft / (el.scrollWidth / STEPS.length))
      setActive(i)
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <section className="story" id="how">
      <div className="wrap">
        <div className="sec-head">
          <div className="eyebrow">How it works</div>
          <h2>One Sunday becomes <em>a whole week</em> with God's Word.</h2>
          <p>Your church already preaches. We make sure it doesn't stop at the car park.</p>
        </div>
        <div className="story-grid">
          <div className="story-phone" aria-hidden="true">
            <div className="phone"><div className="scr"><div className="notch"/><StoryScreen step={active}/></div></div>
          </div>
          <div className="steps" ref={stepsEl}>
            {STEPS.map((s, i) => (
              <div key={s.day} data-s={i} ref={(el) => { stepRefs.current[i] = el }} className={`step${i === active ? ' on' : ''}`}>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
                <div className="mini-phone" aria-hidden="true"><div className="phone"><div className="scr"><div className="notch"/><StoryScreen step={i}/></div></div></div>
              </div>
            ))}
          </div>
          <div className="dots" aria-hidden="true">
            {STEPS.map((s, i) => <i key={s.day} className={i === active ? 'on' : ''}/>)}
          </div>
        </div>
      </div>
    </section>
  )
}

// One mock screen per step — a simplified version of the real app surface
// that step describes, not a pixel-accurate re-render of the app itself.
function StoryScreen({ step }) {
  if (step === 0) {
    return (
      <>
        <div className="sb"><span>Sun 12:05</span><span>●●● ▮</span></div>
        <div className="app-pad" style={{ paddingTop: 26 }}>
          <div className="eyebrow" style={{ fontSize: 10.5 }}>Leaders · New Life Church</div>
          <div style={{ fontSize: 19, fontWeight: 800, marginTop: 6, lineHeight: 1.2 }}>This week's Bible plan, generated from today's sermon</div>
          <div className="thumb" style={{ height: 138, marginTop: 14 }}/>
          <div style={{ marginTop: 10, fontWeight: 800 }}>All things for good</div>
          <div style={{ color: 'var(--stone)', fontSize: 12 }}>Romans 8:28–39 · 34 min · found at 41:10 in the livestream</div>
          <div style={{ marginTop: 12, background: 'var(--paper)', borderRadius: 14, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="row" style={{ justifyContent: 'space-between' }}><b style={{ fontSize: 12.5 }}>Bible moments found</b><b style={{ color: 'var(--clip-ink)' }}>23</b></div>
            <div className="row" style={{ justifyContent: 'space-between' }}><b style={{ fontSize: 12.5 }}>5-day Bible plan ready</b><b style={{ color: 'var(--gold-ink)' }}>✓</b></div>
          </div>
          <span className="btn btn-ink" style={{ marginTop: 14, width: '100%', justifyContent: 'center', padding: 12 }}>Review and publish</span>
        </div>
      </>
    )
  }
  if (step === 1) {
    return (
      <>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(170deg,#3b4a6b,#a8817a 70%,#E8C87A)' }}/>
        <div style={{ position: 'relative', color: '#fff', textAlign: 'center', paddingTop: 64 }}>
          <div style={{ fontSize: 15, fontWeight: 600, opacity: .9 }}>Monday 3 November</div>
          <div style={{ fontSize: 66, fontWeight: 300, lineHeight: 1 }}>7:30</div>
        </div>
        <div style={{ position: 'relative', margin: '26px 12px 0', background: 'rgba(255,253,249,.92)', borderRadius: 20, padding: '12px 14px', display: 'flex', gap: 10 }}>
          <span style={{ width: 34, height: 34, borderRadius: 9, background: '#E8C87A', flexShrink: 0 }}/>
          <div>
            <div className="row" style={{ justifyContent: 'space-between' }}><b style={{ fontSize: 13 }}>The Bible Community</b><small style={{ color: 'var(--stone)' }}>now</small></div>
            <div style={{ fontWeight: 800, fontSize: 13, marginTop: 2 }}>This week's plan is ready: Romans 8</div>
            <div style={{ fontSize: 12.5, color: 'var(--ink2)' }}>From Sunday at New Life. 5 short readings.</div>
          </div>
        </div>
        <div style={{ position: 'relative', margin: '10px 12px 0', background: 'rgba(255,253,249,.75)', borderRadius: 20, padding: '12px 14px', fontSize: 12.5 }}>
          <b>Day 1 · Romans 8:28–30</b><br/><span style={{ color: 'var(--ink2)' }}>5 minutes with this week's passage.</span>
        </div>
      </>
    )
  }
  if (step === 2) {
    const days = ['1', '2', '3', '4', '5']
    return (
      <>
        <div className="sb"><span>9:41</span><span>●●● ▮</span></div>
        <div className="app-pad" style={{ paddingTop: 20 }}>
          <div className="row" style={{ gap: 6 }}>
            {days.map((d, i) => (
              <span key={d} style={{ flex: 1, textAlign: 'center', borderRadius: 10, padding: '7px 0', fontWeight: 800, fontSize: 12, background: i < 2 ? 'var(--ink)' : (i === 2 ? 'var(--gold)' : 'var(--paper)'), color: i < 2 ? 'var(--cream)' : 'inherit' }}>{i < 2 ? '✓' : `Day ${d}`}</span>
            ))}
          </div>
          <div style={{ fontSize: 19, fontWeight: 800, marginTop: 14 }}>Day 3 · Romans 8:31–34</div>
          <div style={{ color: 'var(--stone)', fontSize: 12 }}>You finished today. Here's what your church shared.</div>
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[['S', '#F3C6C6', 'Sarah', 'Verse 31 on repeat this week.', '♡ 6 · Reply'], ['J', '#C9DDF3', 'James', 'Loved how Pete linked this to Psalm 118 on Sunday.', '♡ 3 · 2 replies'], ['A', '#BFE0C2', 'Amy', 'Praying this for my mum today.', '♡ 9']].map(c => (
              <div className="cm" key={c[2]} style={{ background: 'var(--paper)', borderRadius: 14, padding: 10 }}>
                <span className="av" style={{ background: c[1] }}>{c[0]}</span>
                <div><b>{c[2]}</b><p>{c[3]}</p><small style={{ color: 'var(--stone)' }}>{c[4]}</small></div>
              </div>
            ))}
          </div>
        </div>
      </>
    )
  }
  return (
    <>
      <div className="sb"><span>9:41</span><span>●●● ▮</span></div>
      <div className="reader-top"><span className="pill">Romans 8 ▾</span><span className="rbtn">Aa</span></div>
      <div className="app-pad verse" style={{ fontSize: 15 }}>
        <sup>31</sup><span className="hl">What then shall we say about these things? If God is for us, who can be against us?</span><span className="dot gold"/> <sup>32</sup>He who didn't spare his own Son…
      </div>
      <div className="sheet" style={{ paddingBottom: 16 }}>
        <div className="grab"/>
        <div className="eyebrow" style={{ fontSize: 10.5, color: 'var(--clip-ink)' }}>Clips on this verse · 12</div>
        <div className="thumb img" style={{ height: 120, marginTop: 10, backgroundImage: `url(${NL_THUMB_URL})` }}/>
        <div className="row" style={{ marginTop: 8, gap: 6 }}><span style={{ background: 'var(--clip)', color: 'var(--clip-ink)', fontSize: 10.5, fontWeight: 800, borderRadius: 999, padding: '3px 8px' }}>Clip</span><b style={{ fontSize: 12.5 }}>New Life · from 18:20</b></div>
        <div style={{ fontSize: 12, color: 'var(--stone)', marginTop: 4 }}>"If God is for us" · Pete, 26 Oct</div>
        <div style={{ marginTop: 10, fontWeight: 800, fontSize: 12.5, border: '1.5px solid var(--gold)', borderRadius: 999, padding: 8, textAlign: 'center' }}>Open full sermon →</div>
      </div>
    </>
  )
}

// ── Try it — verse tap drives a clips/comments panel ────────────────────
const TRY_VERSES = {
  28: { ref: 'Romans 8:28', clips: 43, comments: 2 },
  31: { ref: 'Romans 8:31', clips: 12, comments: 3 },
}
const TRY_COMMENTS = {
  28: [
    { init: 'T', bg: '#C9DDF3', name: 'Tom', text: 'This verse carried me through a hard month.' },
    { init: 'M', bg: '#BFE0C2', name: 'Mia', text: "Same. Pete's point on Sunday really helped.", me: true },
  ],
  31: [
    { init: 'S', bg: '#F3C6C6', name: 'Sarah', text: 'Reading this before my exam tomorrow.' },
    { init: 'J', bg: '#C9DDF3', name: 'James', text: "Praying for you! You've got this." },
    { init: 'S', bg: '#F3C6C6', name: 'Sarah', text: 'Thank you!', me: true },
  ],
}

function TryIt() {
  const [verse, setVerse] = useState('28')
  const [tab, setTab] = useState('clips')
  const [ripples, setRipples] = useState([])
  const [callout, setCallout] = useState({ show: false, text: '', left: 0, top: 0, ax: 0 })
  const sectionRef = useRef(null)
  const cardRef = useRef(null)
  const v28Ref = useRef(null)
  const v31Ref = useRef(null)
  const userTookRef = useRef(false)
  const startedRef = useRef(false)
  const demoTimerRef = useRef(null)
  const calloutTimerRef = useRef(null)

  function pick(v, defaultTab) { setVerse(v); setTab(defaultTab) }
  const d = TRY_VERSES[verse]

  // Auto-demo: taps verse 28 (clips) then 31 (comments) on a loop, with a
  // ripple + callout tooltip, until the visitor interacts with the section.
  useEffect(() => {
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const sectionEl = sectionRef.current
    if (!sectionEl) return undefined

    function doRipple(el) {
      if (reduce || !el || !cardRef.current) return
      const target = el.querySelector('.dot,.cbadge') || el
      const tr = target.getBoundingClientRect()
      const c = cardRef.current.getBoundingClientRect()
      const id = Math.random().toString(36).slice(2)
      setRipples((rs) => [...rs, { id, left: tr.left + tr.width / 2 - c.left, top: tr.top + tr.height / 2 - c.top }])
      setTimeout(() => setRipples((rs) => rs.filter((r) => r.id !== id)), 800)
    }

    function doCallout(el, text) {
      if (!el || !cardRef.current) return
      const target = el.querySelector('.dot,.cbadge') || el
      const tr = target.getBoundingClientRect()
      const c = cardRef.current.getBoundingClientRect()
      const x = tr.left + tr.width / 2 - c.left
      const w = 260
      const left = Math.max(12, Math.min(x - 28, c.width - w - 12))
      setCallout({ show: true, text, left, top: tr.bottom - c.top + 14, ax: x - left - 6 })
      clearTimeout(calloutTimerRef.current)
      calloutTimerRef.current = setTimeout(() => setCallout((cu) => ({ ...cu, show: false })), 2600)
    }

    function step(i) {
      if (userTookRef.current) return
      const el = i % 2 ? v31Ref.current : v28Ref.current
      doCallout(el, i % 2 ? 'Tap 💬 to read what your church says' : 'Tap a purple dot to watch the sermon clip')
      setTimeout(() => doRipple(el), 700)
      setTimeout(() => { if (!userTookRef.current) pick(i % 2 ? '31' : '28', i % 2 ? 'comments' : 'clips') }, 950)
      demoTimerRef.current = setTimeout(() => step(i + 1), i % 2 ? 4200 : 6200)
    }

    function stopDemo() {
      userTookRef.current = true
      clearTimeout(demoTimerRef.current)
      setCallout((cu) => ({ ...cu, show: false }))
    }
    sectionEl.addEventListener('pointerdown', stopDemo, { once: true, capture: true })
    sectionEl.addEventListener('keydown', stopDemo, { once: true, capture: true })

    let io
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !startedRef.current) {
            startedRef.current = true
            setTimeout(() => step(0), 600)
          }
        })
      }, { threshold: 0.4 })
      io.observe(sectionEl)
    }

    return () => {
      clearTimeout(demoTimerRef.current)
      clearTimeout(calloutTimerRef.current)
      sectionEl.removeEventListener('pointerdown', stopDemo, { capture: true })
      sectionEl.removeEventListener('keydown', stopDemo, { capture: true })
      if (io) io.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <section id="try" ref={sectionRef}>
      <div className="wrap">
        <div className="sec-head">
          <div className="eyebrow">Try it</div>
          <h2>Tap any verse to see what your church <em>says about it.</em></h2>
          <p>Watch sermon clips, and read community comments.</p>
        </div>
        <div className="try-grid">
          <div className="bible-card" ref={cardRef}>
            <div className="ref"><b>Romans 8:28–32</b></div>
            <p>
              <button type="button" ref={v28Ref} className={`v${verse === '28' ? ' sel' : ''}`} onClick={() => pick('28', 'clips')}><sup>28</sup>We know that all things work together for good for those who love God, for those who are called according to his purpose.<span className="dot"/></button>{' '}
              <span className="v" style={{ cursor: 'default' }}><sup>29</sup>For whom he foreknew, he also predestined to be conformed to the image of his Son, that he might be the firstborn among many brothers.</span>{' '}
              <span className="v" style={{ cursor: 'default' }}><sup>30</sup>Whom he predestined, those he also called. Whom he called, those he also justified. Whom he justified, those he also glorified.</span>{' '}
              <button type="button" ref={v31Ref} className={`v${verse === '31' ? ' sel' : ''}`} onClick={() => pick('31', 'comments')}><sup>31</sup>What then shall we say about these things? If God is for us, who can be against us?<span className="cbadge"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z"/></svg>3</span></button>{' '}
              <span className="v" style={{ cursor: 'default' }}><sup>32</sup>He who didn't spare his own Son, but delivered him up for us all, how would he not also with him freely give us all things?</span>
            </p>
            <div className="try-hint"><span className="dot"/> Sermon clips <span className="cbadge" style={{ marginLeft: 14 }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z"/></svg>3</span> Comments from your church</div>
            {ripples.map((r) => <span key={r.id} className="tap" style={{ left: r.left, top: r.top }}/>)}
            <div className={`callout${callout.show ? ' show' : ''}`} style={{ left: callout.left, top: callout.top, '--ax': `${callout.ax}px` }}>{callout.text}</div>
          </div>

          <div className="clips-panel" aria-live="polite">
            <h4>{d.ref}</h4>
            <div className="ptabs">
              <button type="button" className={tab === 'clips' ? 'on' : ''} onClick={() => setTab('clips')}>Sermon clips · {d.clips}</button>
              <button type="button" className={tab === 'comments' ? 'on' : ''} onClick={() => setTab('comments')}>Comments · {d.comments}</button>
            </div>
            {tab === 'clips' ? (
              <>
                <div className="playcard">
                  <div className="vid" style={{ backgroundImage: `url(${NL_THUMB_URL})` }}><div className="bar"><span>❚❚</span><i/><span>1:12</span></div></div>
                  <div className="meta"><span><small>● New Life · 25 Oct</small><b>Disciple as a discipler</b></span><span className="full">Full sermon →</span></div>
                </div>
                <div className="mini" style={{ marginTop: 8, borderTop: 0 }}><span className="thumb" style={{ background: 'linear-gradient(135deg,#26323f,#5d7389)' }}/><span><small>● Grace Chapel · 14 Sep</small><b>When it all falls apart</b><small>Starts at 31:05 · Key moment</small></span></div>
                <div className="mini"><span className="thumb" style={{ background: 'linear-gradient(135deg,#3a2b2b,#8a5d52)' }}/><span><small>● Hope Church · 2 Mar</small><b>The golden chain</b><small>Starts at 8:15</small></span></div>
              </>
            ) : (
              <>
                {TRY_COMMENTS[verse].map((c, i) => (
                  <div className={`cbub${c.me ? ' me' : ''}`} key={i}>
                    {!c.me && <span className="av" style={{ background: c.bg }}>{c.init}</span>}
                    <span className="b"><small>{c.name}</small>{c.text}</span>
                    {c.me && <span className="av" style={{ background: c.bg }}>{c.init}</span>}
                  </div>
                ))}
                <div className="example" style={{ marginTop: 4 }}>Only people at your church see these.</div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

function Community() {
  return (
    <section id="community">
      <div className="wrap">
        <div className="sec-head">
          <div className="eyebrow">Community</div>
          <h2>Read the Bible <em>with the people you worship with.</em></h2>
          <p>Not with strangers on the internet. Everything stays inside your church, and your leaders can moderate it.</p>
        </div>
        <div className="cards3">
          <div className="ccard c-gold">
            <h3>Share what stood out</h3>
            <p>Comment on any verse or plan day.</p>
            <div className="demo bubbles">
              <div className="bub"><span className="av" style={{ background: '#C9DDF3' }}>T</span><span className="b">That verse got me through this week.</span></div>
              <div className="bub me"><span className="b">Same! Listening back to Sunday's bit again.</span><span className="av" style={{ background: '#BFE0C2' }}>M</span></div>
            </div>
          </div>
          <div className="ccard c-rose">
            <h3>Pause &amp; pray</h3>
            <p>Ask for prayer, and know who prayed.</p>
            <div className="demo bubbles">
              <div className="bub"><span className="av" style={{ background: '#F3D3D3' }}>A</span><span className="b">Interview on Thursday. Feeling nervous.</span></div>
              <div className="chip">🙏 9 people prayed</div>
              <div className="bub"><span className="av" style={{ background: '#F3D3D3' }}>A</span><span className="b">It went well. Thank you!</span></div>
            </div>
          </div>
          <div className="ccard c-sky">
            <h3>Highlight, note, share</h3>
            <p>Keep what matters and pass it on.</p>
            <div className="demo bubbles">
              <div className="vh"><span className="hlx">If God is for us, who can be against us?</span></div>
              <div className="sws"><i style={{ background: '#F2D98A', boxShadow: '0 0 0 2px #fff,0 0 0 4px #1F1B16' }}/><i style={{ background: '#BFE0C2' }}/><i style={{ background: '#C9DDF3' }}/><i style={{ background: '#F3C6C6' }}/><span>Share ↗</span></div>
              <div className="bub me"><span className="b">🔒 Read this again before Thursday.</span></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function Serving() {
  return (
    <section id="serving">
      <div className="wrap">
        <div className="sec-head">
          <div className="eyebrow">Serving</div>
          <h2>Rotas that <em>sort themselves out.</em></h2>
          <p>Tell us your teams and the rules once. We ask everyone for their away dates, fill the rota fairly, and remind people the day before.</p>
        </div>
        <div className="cards3">
          <div className="ccard c-lav">
            <h3>You're on this Sunday</h3>
            <p>See when you're serving on This Week, and reply in a tap.</p>
            <div className="demo">
              <div className="rs-k">YOU'RE SERVING THIS SUNDAY</div>
              <div className="rs-t">Welcome team · 10:00</div>
              <div className="rs-s">With Sarah and Jamie</div>
              <div className="rs-btns"><span className="rs-b dark">✓ I'll be there</span><span className="rs-b">Can't make it</span></div>
              <div className="chip" style={{ marginLeft: 0 }}>Swap suggested: Jamie is free</div>
            </div>
          </div>
          <div className="ccard c-gold">
            <h3>Away dates, asked for you</h3>
            <p>Before each quarter, everyone is asked which Sundays they're away.</p>
            <div className="demo">
              <div className="rs-t" style={{ fontSize: 15 }}>Which Sundays are you away?</div>
              <div className="rs-days"><span>Sun 3</span><span>Sun 10</span><span className="aw">✈ Sun 17</span><span className="aw">✈ Sun 24</span><span>Sun 31</span></div>
              <div className="rs-s">9 of 12 replied · reminder sent to the rest</div>
            </div>
          </div>
          <div className="ccard c-sky">
            <h3>Filled fairly, in seconds</h3>
            <p>Leaders set the rules. The app plans up to six months ahead.</p>
            <div className="demo">
              <div className="rs-rule">✓ Skip people's away dates</div>
              <div className="rs-rule">✓ No more than 1 in 3 weeks each</div>
              <div className="rs-rule">✓ Keep couples on the same Sunday</div>
              <div className="rs-ok">13 Sundays filled · 1 gap · 0 clashes</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// ── Bible map demo — Riverside Church, an invented example church with
// invented numbers (docs/FIX-WEBSITE.md: no real platform stats on the page).
const NT = [['Matthew', 28, 9], ['Mark', 16, 12], ['Luke', 24, 8], ['John', 21, 22], ['Acts', 28, 15], ['Romans', 16, 18], ['1 Corinthians', 16, 6], ['2 Corinthians', 13, 3], ['Galatians', 6, 5], ['Ephesians', 6, 9], ['Philippians', 4, 7], ['Colossians', 4, 4], ['1 Thessalonians', 5, 6], ['2 Thessalonians', 3, 1], ['1 Timothy', 6, 2], ['2 Timothy', 4, 3], ['Titus', 3, 1], ['Philemon', 1, 1], ['Hebrews', 13, 8], ['James', 5, 5], ['1 Peter', 5, 6], ['2 Peter', 3, 1], ['1 John', 5, 4], ['2 John', 1, 0], ['3 John', 1, 0], ['Jude', 1, 1], ['Revelation', 22, 4]]
const OT = [['Genesis', 50, 11], ['Exodus', 40, 6], ['Leviticus', 27, 1], ['Numbers', 36, 1], ['Deuteronomy', 34, 2], ['Joshua', 24, 3], ['Judges', 21, 2], ['Ruth', 4, 4], ['1 Samuel', 31, 3], ['2 Samuel', 24, 2], ['1 Kings', 22, 2], ['2 Kings', 25, 1], ['1 Chronicles', 29, 0], ['2 Chronicles', 36, 1], ['Ezra', 10, 0], ['Nehemiah', 13, 5], ['Esther', 10, 4], ['Job', 42, 2], ['Psalms', 150, 14], ['Proverbs', 31, 3], ['Ecclesiastes', 12, 1], ['Song of Songs', 8, 0], ['Isaiah', 66, 7], ['Jeremiah', 52, 2], ['Lamentations', 5, 1], ['Ezekiel', 48, 1], ['Daniel', 12, 5], ['Hosea', 14, 1], ['Joel', 3, 1], ['Amos', 9, 1], ['Obadiah', 1, 0], ['Jonah', 4, 4], ['Micah', 7, 2], ['Nahum', 3, 0], ['Habakkuk', 3, 1], ['Zephaniah', 3, 0], ['Haggai', 2, 1], ['Zechariah', 14, 0], ['Malachi', 4, 1]]
const HEAT_RAMP = ['#3a332c', '#5a4a33', '#7d6532', '#a8853a', '#d1ab55', '#E8C87A']
const BOOK_ABBR = { '1 Corinthians': '1 Cor', '2 Corinthians': '2 Cor', '1 Thessalonians': '1 Thess', '2 Thessalonians': '2 Thess', Philippians: 'Phil', Colossians: 'Col', Revelation: 'Rev', Ephesians: 'Eph', Galatians: 'Gal', Deuteronomy: 'Deut', Ecclesiastes: 'Eccl', 'Song of Songs': 'Song', Lamentations: 'Lam', '1 Chronicles': '1 Chron', '2 Chronicles': '2 Chron', Zephaniah: 'Zeph', Zechariah: 'Zech', Habakkuk: 'Hab', Leviticus: 'Lev', Numbers: 'Num', Nehemiah: 'Neh', Obadiah: 'Obad', Proverbs: 'Prov', Jeremiah: 'Jer', Hebrews: 'Heb', Philemon: 'Phlm', Malachi: 'Mal' }
const SERMON_TITLES = ['Grace that changes everything', 'Sent out with nothing', 'When God feels far', 'The God who keeps his promises', 'Hope while we wait', 'Love that costs something', 'Standing firm', 'A new kind of family', 'Rest for the weary', 'Courage to follow']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Sep', 'Oct', 'Nov']
const THUMB_GRADIENTS = ['#2c2620,#6b5640', '#1d2a33,#56707f', '#3a2b2b,#8a5d52', '#20304a,#4a6a9a']

function heat(n) { return n <= 0 ? 0 : n <= 2 ? 1 : n <= 4 ? 2 : n <= 7 ? 3 : n <= 12 ? 4 : 5 }
function abbr(book) { return BOOK_ABBR[book] || book }
// Deterministic pseudo-random generator, seeded per book name, so the demo
// numbers are stable across renders instead of reshuffling on every tap.
function seeded(str) {
  let h = 0
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0
  return () => { h = (h * 1103515245 + 12345) >>> 0; return h / 4294967296 }
}

function ForChurches() {
  const [testament, setTestament] = useState('nt')
  const [selected, setSelected] = useState('Romans')
  const list = testament === 'nt' ? NT : OT

  function pickTestament(t) {
    setTestament(t)
    setSelected(t === 'nt' ? 'Romans' : 'Psalms')
  }

  const detail = useMemo(() => {
    const book = NT.concat(OT).find(b => b[0] === selected)
    if (!book) return null
    const [name, chapterCount, sermonCount] = book
    const rand = seeded(name)
    const chapters = []
    for (let i = 1; i <= chapterCount; i++) chapters.push(sermonCount ? Math.max(0, Math.round((rand() - 0.45) * sermonCount * 0.9)) : 0)
    let topChapter = 0
    chapters.forEach((v, k) => { if (v > chapters[topChapter]) topChapter = k })
    const sermons = []
    if (sermonCount) {
      const preached = chapters.map((v, k) => (v ? k + 1 : null)).filter(Boolean)
      const n = Math.min(3, sermonCount)
      for (let i = 0; i < n; i++) {
        const ch = preached.length ? preached[Math.floor(rand() * preached.length)] : 1 + Math.floor(rand() * chapterCount)
        sermons.push({
          gradient: THUMB_GRADIENTS[i % 4],
          title: SERMON_TITLES[Math.floor(rand() * SERMON_TITLES.length)],
          ref: `${name} ${ch}`,
          date: `${1 + Math.floor(rand() * 27)} ${MONTHS[Math.floor(rand() * MONTHS.length)]}`,
        })
      }
    }
    return { name, chapterCount, sermonCount, chapters, topChapter, sermons }
  }, [selected])

  return (
    <section className="map" id="map">
      <div className="wrap">
        <div className="sec-head">
          <div className="eyebrow">For church leaders</div>
          <h2>Turn Sunday into a week in the Bible, <em>automatically.</em></h2>
          <p>Connect the YouTube channel you already use. We make a Bible plan from every new sermon and map your past preaching across the whole Bible. Here's an example church: tap a book.</p>
        </div>
        <div className="mapdemo">
          <div className="md-left">
            <div className="md-church"><b>Riverside Church</b><span>3 years of Sunday sermons</span></div>
            <div className="md-tabs" role="tablist" aria-label="Testament">
              <button type="button" className={testament === 'nt' ? 'on' : ''} onClick={() => pickTestament('nt')}>New Testament</button>
              <button type="button" className={testament === 'ot' ? 'on' : ''} onClick={() => pickTestament('ot')}>Old Testament</button>
            </div>
            <div className="md-grid">
              {list.map(([name, , sermonCount]) => {
                const h = heat(sermonCount)
                return (
                  <button type="button" key={name} className={`md-b${name === selected ? ' sel' : ''}`}
                    style={{ background: HEAT_RAMP[h], color: h >= 4 ? '#1F1B16' : '#E8E0D4' }}
                    aria-label={`${name}, ${sermonCount} sermons`}
                    onClick={() => setSelected(name)}>
                    {abbr(name)}
                  </button>
                )
              })}
            </div>
            <div className="md-key"><span>Not preached</span><span className="scale">{HEAT_RAMP.map(c => <i key={c} style={{ background: c }}/>)}</span><span>Most preached</span></div>
          </div>
          <div className="md-right" aria-live="polite">
            {detail && (
              <>
                <h3>{detail.name}</h3>
                <div className="sub">Riverside Church · {detail.sermonCount ? `${detail.sermonCount} sermon${detail.sermonCount === 1 ? '' : 's'}${detail.chapters[detail.topChapter] ? ` · most on chapter ${detail.topChapter + 1}` : ''}` : 'not preached yet'}</div>
                <div className="md-lab">CHAPTERS</div>
                <div className="md-ch">
                  {detail.chapters.map((v, k) => {
                    const h = heat(v * 2)
                    return <span key={k} style={{ background: v ? ['#EFE8DD', '#E8DDC4', '#E2CF9E', '#D8BC73', '#CDA94B', '#C29B45'][h] : '#EFE8DD', color: h >= 4 ? '#1F1B16' : '#8A8177' }}>{k + 1}</span>
                  })}
                </div>
                {detail.sermonCount ? (
                  <>
                    <div className="md-lab">SERMONS</div>
                    {detail.sermons.map((s, i) => (
                      <div className="md-s" key={i}>
                        <span className="th" style={{ background: `linear-gradient(135deg,${s.gradient})` }}/>
                        <span><b>{s.title}</b><small>{s.ref} · {s.date}</small></span>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="md-empty">Nothing preached from {detail.name} yet. Leaders can see the gaps and plan a series.</div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

function ChurchesSetup() {
  return (
    <section className="churches" id="churches">
      <div className="wrap">
        <div className="sec-head">
          <div className="eyebrow">Getting started</div>
          <h2>Ready before <em>Sunday lunch.</em></h2>
          <p>No new content to write and nothing to upload by hand. If you stream to YouTube, you're nearly done.</p>
        </div>
        <div className="setup">
          <div className="su"><span className="num">1</span><h3>Say hello</h3><p>Tell us your church and your YouTube channel. We do the setup with you.</p><small>A short call</small></div>
          <div className="su"><span className="num">2</span><h3>See it working</h3><p>We bring in your sermons and show you every passage you've preached, before you decide anything.</p><small>With your sermons</small></div>
          <div className="su"><span className="num">3</span><h3>Invite your church</h3><p>Share one link or a QR code on the screen. Members join free, on iPhone, Android or the web.</p><small>This Sunday</small></div>
        </div>
        <div className="free-band" id="app">
          <div>Members never pay. <span>Download the app free, or use it on the web.</span></div>
          <StoreBadges/>
        </div>
      </div>
    </section>
  )
}

const FAQS = [
  { q: 'Is this a social network?', a: ["No. It's a Bible app connected to your church. It helps your church read the Bible together during the week, with a plan from Sunday's sermon and clips of what was preached.", "There's also a community board for your church: prayer requests, what's coming up and who's going, and conversation about the week's reading. It's only for your own church, and it's there to support your services, small groups and time together in person, not replace them."] },
  { q: 'Do our members have to pay?', a: ['No. The church subscribes, and everyone who joins your church uses the app free. Anyone can also follow other churches for free.'] },
  { q: "What if we don't stream on YouTube?", a: ['For now we work with sermons on YouTube, including unlisted videos. If you use another service, tell us and we’ll let you know when it’s supported.'] },
  { q: 'Who checks the weekly plan before it goes out?', a: ['Your leaders do. Each Sunday evening they get the draft plan and approve or edit it with one tap. Nothing is published without them.'] },
  { q: 'Is our community private?', a: ["Yes. Comments, prayers and plan discussions are only visible to your church's members. Leaders can remove posts and block people from the Leaders dashboard."] },
  { q: 'How do we get started?', a: ["Get in touch and we'll show you around using your own sermons. We connect your YouTube, map what you've preached and get your first weekly plan ready for your leaders to check. Then you invite your church with one link."] },
]

function Faq() {
  return (
    <section id="faq">
      <div className="wrap faq-grid">
        <div className="sec-head">
          <div className="eyebrow">Questions</div>
          <h2>Good questions <em>to ask.</em></h2>
        </div>
        <div>
          {FAQS.map((f, i) => (
            <details key={f.q} open={i === 0}>
              <summary>{f.q}</summary>
              {f.a.map((p, j) => <p key={j}>{p}</p>)}
            </details>
          ))}
        </div>
      </div>
    </section>
  )
}

function LeadForm() {
  const emailRef = useRef(null)
  const [sending, setSending] = useState(false)
  const [toast, setToast] = useState({ show: false, msg: '', err: false })
  const toastTimer = useRef(null)

  function showToast(msg, isErr) {
    setToast({ show: true, msg, err: !!isErr })
    clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast((t) => ({ ...t, show: false })), 4500)
  }

  async function submit(e) {
    e.preventDefault()
    const em = emailRef.current
    if (!em.value || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em.value)) {
      em.setAttribute('aria-invalid', 'true')
      em.placeholder = 'Enter an email address like you@church.org'
      em.value = ''
      em.focus()
      return
    }
    const emailVal = em.value
    setSending(true)
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/submit-church-enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ name: 'Not specified', email: emailVal, church: 'Not specified', type: 'new_church' }),
      })
      const data = await res.json().catch(() => ({}))
      setSending(false)
      if (!res.ok || data?.error) { showToast('Something went wrong — please email hello@thebiblecommunity.co.uk instead.', true); return }
      showToast(`Thanks — we'll be in touch at ${emailVal}.`)
      em.value = ''
      em.removeAttribute('aria-invalid')
    } catch {
      setSending(false)
      showToast('Something went wrong — please email hello@thebiblecommunity.co.uk instead.', true)
    }
  }

  return (
    <section className="dawn" id="start">
      <div className="dawn-sun" aria-hidden="true"/>
      <div className="wrap">
        <div className="eyebrow" style={{ color: 'inherit', opacity: .8 }}>For church leaders</div>
        <h2>Bring your church's sermons into the Bible. <em>We'd love to show you around.</em></h2>
        <p>Enter your email and we'll be in contact.</p>
        <form className="lead-form" onSubmit={submit} noValidate>
          <label htmlFor="lp-email">Your email</label>
          <input ref={emailRef} id="lp-email" name="email" type="email" placeholder="Enter your email" autoComplete="email" required/>
          <button className="btn btn-ink" type="submit" disabled={sending}>{sending ? 'Sending…' : 'Show me around →'}</button>
        </form>
      </div>
      <div className={`toast${toast.show ? ' show' : ''}${toast.err ? ' toast-err' : ''}`} role="status" aria-live="polite">{toast.msg}</div>
    </section>
  )
}

function Footer({ onNavigate, scrollToId }) {
  return (
    <footer>
      <div className="wrap foot">
        <span>
          © {new Date().getFullYear()} The Bible Community · Made in Tunbridge Wells<br/>
          <small style={{ fontSize: 12 }}>The Bible Community is run by Sonrise Studios Ltd, registered in England and Wales, company number 15999485. Registered office: Whiteoaks, Vicarage Road, Tunbridge Wells, England, TN4 0SN.</small>
        </span>
        <nav aria-label="Footer">
          <a href="#churches" onClick={scrollToId('churches')}>For churches</a>
          <a href="#app" onClick={scrollToId('app')}>Get the app</a>
          <a href="/privacy" onClick={(e) => { e.preventDefault(); onNavigate?.('/privacy') }}>Privacy</a>
          <a href="/terms" onClick={(e) => { e.preventDefault(); onNavigate?.('/terms') }}>Terms</a>
          <a href="#start" onClick={scrollToId('start')}>Contact</a>
        </nav>
      </div>
    </footer>
  )
}
